import { clinicApi } from "@api";
import { enumFilter, initPage } from "@configs";
import { IClinic, IClinicSearch, IPagination } from "@interfaces";
import { setLoading } from "@redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { getApiErrorMessage } from "../apiError";
import { useNotify } from "../notifyHook";

export const clinicQueryKeys = {
  all: ["clinics"] as const,
  lists: () => [...clinicQueryKeys.all, "list"] as const,
  list: (params: IClinicSearch) => [...clinicQueryKeys.lists(), params] as const,
  details: () => [...clinicQueryKeys.all, "detail"] as const,
  detail: (id: string) => [...clinicQueryKeys.details(), id] as const,
};

const fetchClinics = async (params: IClinicSearch) => {
  const res = await clinicApi.clinics(params);
  return {
    data: res.data?.data as IClinic[],
    total: res.data?.nTotal as number,
  };
};

export const useClinicDetail = (id?: string) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { error } = useNotify();

  const query = useQuery({
    queryKey: clinicQueryKeys.detail(id ?? ""),
    queryFn: async () => {
      const res = await clinicApi.clinic(id as string);
      return res.data as IClinic;
    },
    enabled: Boolean(id),
  });

  useEffect(() => {
    dispatch(setLoading(query.isFetching));
  }, [dispatch, query.isFetching]);

  useEffect(() => {
    if (query.isError) {
      error(getApiErrorMessage(query.error, t("message.fetch.fail")));
    }
  }, [query.isError, query.error, error, t]);

  return query;
};

export const useClinicListPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { error } = useNotify();

  const [page, setPage] = useState<IPagination>(initPage);
  const [filters, setFilters] = useState<enumFilter | string>("");
  const [text, setText] = useState("");
  const [date, setDate] = useState<string[]>([]);

  const params: IClinicSearch = {
    page: page.page,
    limit: page.limit,
    searchColumn: filters,
    search: text,
    startDate: date[0],
    endDate: date[1],
  };

  const query = useQuery({
    queryKey: clinicQueryKeys.list(params),
    queryFn: () => fetchClinics(params),
    placeholderData: (previousData) => previousData,
  });

  useEffect(() => {
    dispatch(setLoading(query.isFetching));
  }, [dispatch, query.isFetching]);

  useEffect(() => {
    if (query.isError) {
      error(getApiErrorMessage(query.error, t("message.fetch.fail")));
    }
  }, [query.isError, query.error, error, t]);

  const handleDateFilter = (_dates: null | (Dayjs | null)[], dateStrings: string[]) => {
    setDate(dateStrings);
  };

  const handlePagination = (current: number, size: number) => {
    setPage({ ...page, page: current, limit: size });
  };

  const changeFilter = (value: string) => setFilters(value);
  const searchText = (value: string) => setText(value);

  const handleClearSearch = () => {
    setText("");
    setFilters("");
  };

  const handleClearDate = () => setDate([]);

  return {
    data: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    page,
    filters,
    text,
    date,
    refetch: query.refetch,
    handleDateFilter,
    handlePagination,
    changeFilter,
    searchText,
    handleClearSearch,
    handleClearDate,
  };
};

export const useCreateClinic = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: IClinic) => clinicApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clinicQueryKeys.lists() });
    },
  });
};

export const useUpdateClinic = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload, id }: { payload: IClinic; id: string }) =>
      clinicApi.update(payload, id),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: clinicQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: clinicQueryKeys.detail(id) });
    },
  });
};
